import { createEpstScoreSheet } from 'api/certifications/epst/questions';
import { uploadEpstSpeaking } from 'api/files/files';
import { certificationActions } from 'store/slices/certification/certificationSilce';
import { errorNotification, handleApiError } from 'utils';

/**
 * @hook useEpstSubmission
 * @description Maneja la lógica de envío de respuestas del examen EPST, incluyendo preguntas de opción múltiple,
 * redacción (writing), y grabación de audio (speaking). También gestiona la subida de audios y creación de respuestas en el backend.
 *
 * @param {Object} params
 * @param {number} params.step - Paso actual del examen.
 * @param {Object} params.selectedAnswer - Respuesta seleccionada por el usuario.
 * @param {Object} params.certification - Objeto de certificación actual.
 * @param {string} params.token - Token de autenticación del usuario.
 * @param {Function} params.dispatch - Función de dispatch de Redux.
 * @param {Object} params.currentQuestion - Pregunta actual mostrada.
 * @param {Function} params.setSelectedAnswer - Setter para limpiar o actualizar la respuesta seleccionada.
 * @param {Function} params.setQuestions - Setter para actualizar el arreglo de preguntas pendientes.
 * @param {Object} params.sectionFormRef - Ref hacia el formulario que controla la grabación.
 * @param {Function} params.setBlocking - Setter para bloquear/desbloquear el botón de submit.
 * @param {Function} params.setLoading - Setter para mostrar u ocultar loading.
 *
 * @returns {Object} handlers - Funciones de envío de respuestas:
 * - confirmEmptyAnswer
 * - handleCreateScoreSheetEpst
 * - fetchUploadSpeaking
 * - stopAudioIfRecording
 * - handleSubmitNoAnswer
 * - handleSubmitAnswer
 */
const useEpstSubmission = ({
	step,
	selectedAnswer,
	certification,
	token,
	dispatch,
	currentQuestion,
	setSelectedAnswer,
	setQuestions,
	sectionFormRef,
	setBlocking,
	setLoading
}) => {

	/**
	 * Muestra una alerta de confirmación cuando el usuario intenta enviar una respuesta vacía.
	 *
	 * @param {string} title - Título del modal.
	 * @param {string} text - Texto descriptivo del modal.
	 * @returns {Promise<boolean>} True si el usuario confirma continuar, false si cancela.
	 */
	const confirmEmptyAnswer = async (title, text) => {
		const Swal = (await import('sweetalert2')).default;
		const result = await Swal.fire({
			title,
			text,
			icon: 'warning',
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			reverseButtons: true
		});
		return result.isConfirmed;
	};

	/**
	 * Crea un registro de respuesta en la base de datos y avanza a la siguiente pregunta.
	 *
	 * @param {Object} payload - Datos necesarios para registrar la respuesta.
	 */
	const handleCreateScoreSheetEpst = async (payload) => {
		try {
			const { data } = await createEpstScoreSheet(payload, token);
			if (data?.status === 201) {
				setSelectedAnswer(null);
				setQuestions(prev => prev.slice(1));
				dispatch(certificationActions.setStartTimer(false));
				dispatch(certificationActions.setTimeOver(false));
				setTimeout(() => {
					dispatch(certificationActions.setStartTimer(true));
				}, 50);
			}
		} catch (error) {
			handleApiError(error);
			setBlocking(false);
		} finally {
			setBlocking(false);
		}
	};

	/**
	 * Sube un archivo de audio (speaking) y luego crea el registro de respuesta.
	 *
	 * @param {FormData} form - Formulario con el archivo de audio.
	 * @param {Object} payload - Datos de respuesta para asociar el archivo.
	 */
	const fetchUploadSpeaking = async (form, payload) => {
		try {
			setLoading(true);
			const { data } = await uploadEpstSpeaking(form, token);
			if (data?.status === 200) {
				await handleCreateScoreSheetEpst(payload);
			} else {
				errorNotification('No es posible subir el archivo.');
				setBlocking(false);
			}
		} catch (error) {
			handleApiError(error);
			setBlocking(false);
			setLoading(false);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Detiene la grabación de audio desde el componente hijo y gestiona el envío del archivo.
	 *
	 * @param {Object} payload - Datos necesarios para enviar junto con el archivo de audio.
	 * @returns {Promise<null>}
	 */
	const stopAudioIfRecording = async (payload) => {
		if (step === 10 && sectionFormRef.current?.stopRecordingExternally) {
			try {
				const file = await sectionFormRef.current.stopRecordingExternally();
				payload.answer = file.name;
				const form = new FormData();
				form.append('uploaded_file', file, file.name);
				await fetchUploadSpeaking(form, payload);
			} catch {
				await handleCreateScoreSheetEpst(payload);
			}
		}
		return null;
	};

	/**
	 * Envía una respuesta vacía explícitamente (cuando el usuario no responde).
	 */
	const handleSubmitNoAnswer = () => {
		setBlocking(true);
		let payload = {
			certification_id: certification.id,
			section_id: currentQuestion.section_id,
			question_id: currentQuestion.question_id,
			answer_id: null,
			answer: null
		};
		if (step === 10) return stopAudioIfRecording(payload);
		handleCreateScoreSheetEpst(payload);
	};

	/**
	 * Envía la respuesta del usuario según el tipo de pregunta actual.
	 * Valida si es selección múltiple, redacción o grabación de audio.
	 */
	const handleSubmitAnswer = async () => {
		setBlocking(true);

		const data = {
			certification_id: certification.id,
			section_id: currentQuestion.section_id,
			question_id: currentQuestion.question_id
		};

		// Opción múltiple
		if (selectedAnswer?.answer_id) {
			data.answer_id = selectedAnswer.answer_id;
			return handleCreateScoreSheetEpst(data);
		}

		// Redacción
		if (step === 8) {
			const value = selectedAnswer?.value?.trim();
			if (!value) {
				const confirm = await confirmEmptyAnswer(
					"Respuesta vacía",
					"Estás por enviar una respuesta vacía. Esto puede afectar tu puntaje."
				);
				if (!confirm) return setBlocking(false);
			}
			data.answer = value || "";
			return handleCreateScoreSheetEpst(data);
		}

		// Grabación de audio
		if (step === 10) {
			const isEmpty = !selectedAnswer || Object.keys(selectedAnswer).length === 0;
			if (isEmpty) return await stopAudioIfRecording(data); // ✅ con await
			
			try {
				const file = selectedAnswer?.file;
				if (!file) throw new Error("Archivo de audio no disponible.");
				data.answer = file.name;
				const form = new FormData();
				form.append("uploaded_file", file, file.name);
				return await fetchUploadSpeaking(form, data); // ✅ también con await
			} catch(e) {
				console.error("Error al subir audio:", e);
				return await stopAudioIfRecording(data);
			}
		}

		errorNotification("Es necesario seleccionar una opción.");
		setBlocking(false);
	};

	return {
		confirmEmptyAnswer,
		handleCreateScoreSheetEpst,
		fetchUploadSpeaking,
		stopAudioIfRecording,
		handleSubmitNoAnswer,
		handleSubmitAnswer
	};
};

export default useEpstSubmission;